const DIFFERENT_GROUPINGS_SCHEMA_VERSION = 1;

export const DIFFERENT_GROUPINGS_PROGRESS = Object.freeze({
  INFORMATION_REQUIRED: "information_required",
  INFORMATION_RECEIVED: "information_received",
  RELEASED: "released",
});

const DIFFERENT_GROUPINGS_PROGRESS_ORDER = Object.freeze({
  [DIFFERENT_GROUPINGS_PROGRESS.INFORMATION_REQUIRED]: 0,
  [DIFFERENT_GROUPINGS_PROGRESS.INFORMATION_RECEIVED]: 1,
  [DIFFERENT_GROUPINGS_PROGRESS.RELEASED]: 2,
});

const validProgress = new Set(Object.values(DIFFERENT_GROUPINGS_PROGRESS));

/**
 * @typedef {"information_required" | "information_received" | "released"} DifferentGroupingsProgress
 */

/**
 * @typedef {{ expected: number, complete: number, outstanding: number }} DifferentGroupingsClassroomProgress
 */

/**
 * @typedef {{ status: "nominated" | null, reference: string | null, reNominationSequence: number }} DifferentGroupingsNomination
 */

/**
 * @typedef {{ id: string, name: string, academicYear?: string, grade_level?: string }} DifferentGroupingsConfirmedClassroom
 * @typedef {{ classId: string, displayName: string, teacherId: string | null, teacherName: string | null, status: "setup_required" | "in_progress" }} DifferentGroupingsSetupAssignment
 */

/**
 * @typedef {{ id: string, label: string, defaultClassroomCount: number | null, classroomCount: number | null, confirmedClassrooms?: DifferentGroupingsConfirmedClassroom[], setupAssignments?: DifferentGroupingsSetupAssignment[], classroomProgress: DifferentGroupingsClassroomProgress | null, progress: DifferentGroupingsProgress }} DifferentGroupingsPoint
 */

/**
 * @typedef {{ schemaVersion: number, taskReference: string, schoolId: string, academicYear: string, selectedProgressionPointIds: string[], progressionPoints: DifferentGroupingsPoint[], nomination: DifferentGroupingsNomination, handoffAttempts: number, createdAt: string, updatedAt: string, archivedAt: string | null, progressStatus?: string, scopeStatus?: string }} DifferentGroupingsTask
 */

export const getDifferentGroupingsTaskReference = (schoolId, academicYear) =>
  `different-groupings:${schoolId}:${academicYear}`;

export const getDifferentGroupingsStorageKey = (schoolId, academicYear) =>
  `classroomHandover:differentGroupings:v${DIFFERENT_GROUPINGS_SCHEMA_VERSION}:${schoolId}:${academicYear}`;

export const isValidClassroomCount = (count) =>
  Number.isSafeInteger(count) && count > 0;

export const isValidDifferentGroupingsProgress = (progress) =>
  validProgress.has(progress);

export const canAdvanceDifferentGroupingsProgress = (current, next) =>
  isValidDifferentGroupingsProgress(current) &&
  isValidDifferentGroupingsProgress(next) &&
  DIFFERENT_GROUPINGS_PROGRESS_ORDER[next] >= DIFFERENT_GROUPINGS_PROGRESS_ORDER[current];

const toConfiguredPoint = (point) => ({
  id: point.key,
  label: point.label,
  defaultClassroomCount: isValidClassroomCount(point.countRequiringSetup)
    ? point.countRequiringSetup
    : null,
});

/**
 * The handover entry already supplies territory-configured stable keys and
 * labels. A grade-pair API contract does not yet exist, so this deliberately
 * persists the configured progression point rather than fabricating source or
 * target grade identifiers.
 */
export const normalizeConfiguredProgressionPoints = (progressionPoints = []) => {
  const seen = new Set();
  return progressionPoints.map(toConfiguredPoint).filter((point) => {
    const valid = Boolean(
      point.id && point.label && !seen.has(point.id)
    );
    if (valid) seen.add(point.id);
    return valid;
  });
};

export const createEmptyDifferentGroupingsTask = ({ schoolId, academicYear, now }) => ({
  schemaVersion: DIFFERENT_GROUPINGS_SCHEMA_VERSION,
  taskReference: getDifferentGroupingsTaskReference(schoolId, academicYear),
  schoolId,
  academicYear,
  selectedProgressionPointIds: [],
  progressionPoints: [],
  nomination: {
    status: null,
    reference: null,
    reNominationSequence: 0,
  },
  handoffAttempts: 0,
  createdAt: now,
  updatedAt: now,
  archivedAt: null,
});

export const getDifferentGroupingsTaskState = (task) => {
  const selectedPoints = task?.progressionPoints || [];
  if (selectedPoints.length === 0 || task?.archivedAt) return "draft";
  if (selectedPoints.some((point) => point.progress === DIFFERENT_GROUPINGS_PROGRESS.INFORMATION_REQUIRED)) {
    return "information_required";
  }
  if (selectedPoints.some((point) => point.progress === DIFFERENT_GROUPINGS_PROGRESS.INFORMATION_RECEIVED)) {
    return "information_received";
  }
  return "released";
};
